import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import CardCommon from '@op/components/CardCommon';
import { ThemeContext } from '@home/wrappers/common';
import { SYSTEM_THEME_DARK } from '@home/_config/config';
import useLanguage from '@op/hooks/useLanguage';
import LoadingCommon from '@op/components/LoadingCommon';
import './index.scss';

const themeToParam = (v: string): 'light' | 'dark' =>
  v === SYSTEM_THEME_DARK ? 'dark' : 'light';

const AI_GALLERY_TARGET_ORIGIN =
  typeof window !== 'undefined' ? window.location.origin : '*';

const FIXED_REPORT_URL =
  '/as/report/g-b8a9b7ec-1956-483e-a815-8f978d4b4b29/hot-video-creatives-report.html';

const buildInitialSrc = (
  rawPath: string,
  theme: 'light' | 'dark',
  lang: 'zh' | 'en',
): string => {
  const sep = rawPath.includes('?') ? '&' : '?';
  return `${rawPath}${sep}theme=${theme}&lang=${lang}`;
};

function AIGalleryReportTab() {
  const { themeValue } = useContext(ThemeContext);
  const { isEn } = useLanguage();
  const lang = isEn ? 'en' : 'zh';
  const theme = themeToParam(themeValue);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const [loading, setLoading] = useState(true);
  const reportUrl = FIXED_REPORT_URL;

  const handleIframeLoad = () => {
    setLoading(false);
    try {
      const doc = iframeRef.current?.contentDocument;
      if (doc) {
        const style = doc.createElement('style');
        style.textContent = '.header { display: none; }';
        doc.head.appendChild(style);
      }
    } catch {
      // cross-origin or not ready — ignore
    }
  };

  const initialSrc = useMemo(() => {
    if (!reportUrl) return '';
    return buildInitialSrc(reportUrl, theme, lang);
    // 仅在 path 变化时重算 src（theme/lang 通过 postMessage 同步）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportUrl]);

  useEffect(() => {
    if (!reportUrl) return;
    iframeRef.current?.contentWindow?.postMessage(
      { source: 'databrain-main-frame', type: 'theme', value: theme },
      AI_GALLERY_TARGET_ORIGIN,
    );
  }, [theme, reportUrl]);

  useEffect(() => {
    if (!reportUrl) return;
    iframeRef.current?.contentWindow?.postMessage(
      { source: 'databrain-main-frame', type: 'lang', value: lang },
      AI_GALLERY_TARGET_ORIGIN,
    );
  }, [lang, reportUrl]);

  return (
    <CardCommon
      contentCard
      empty={!initialSrc}
      bodyStyle={{ height: 'calc(100vh - 84px)' }}
      emptyHeight={550}
      className="dbom-aigallery-report"
    >
      {loading && (
        <LoadingCommon
          status="loading"
          isFullPage
        ></LoadingCommon>
      )}
      <iframe
        className='dbom-aigallery-report__iframe'
        src={initialSrc}
        title="ai-gallery-report"
        ref={iframeRef}
        onLoad={handleIframeLoad}
      />
    </CardCommon>
  );
}

export default AIGalleryReportTab;
